import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { Globe, RefreshCw, Trash2, ExternalLink, Github, Info, CheckCircle2, AlertTriangle, Loader2, Star, PenSquare, Plus, Link2, Check, ArrowRightLeft, Upload } from 'lucide-react';
import { useData } from '../../context/DataContext';

const STATUS_META = {
  importing: { label: 'Importing…', icon: Loader2, cls: 'warn', spin: true },
  ready: { label: 'Live', icon: CheckCircle2, cls: 'ok', spin: false },
  error: { label: 'Import failed', icon: AlertTriangle, cls: 'err', spin: false },
};

const Website = ({ lob }) => {
  const darkMode = useDarkMode();
  const { lobs } = useData();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [sites, setSites] = useState([]);
  const [err, setErr] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState('github'); // github | blank | upload
  const [form, setForm] = useState({ name: '', github_owner: '', github_repo: '', github_branch: 'main' });
  const [zipFile, setZipFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [domainDrafts, setDomainDrafts] = useState({}); // { [siteId]: string }
  const [savedDomainId, setSavedDomainId] = useState('');
  const [uploadingId, setUploadingId] = useState('');
  const pollRef = useRef(null);
  const reuploadRefs = useRef({});

  const load = useCallback(() => {
    if (!lob) return;
    api.get(`/websites?lob_id=${lob.id}`).then((r) => {
      setSites(r.data);
      setDomainDrafts((prev) => {
        const next = { ...prev };
        r.data.forEach((s) => { if (next[s.id] === undefined) next[s.id] = s.custom_domain || ''; });
        return next;
      });
    }).catch((e) => setErr(formatApiError(e)));
  }, [lob?.id]);
  useEffect(() => { load(); }, [load]);

  // Poll while any site is still importing.
  useEffect(() => {
    const hasImporting = sites.some((s) => s.status === 'importing');
    if (hasImporting && !pollRef.current) {
      pollRef.current = setInterval(load, 3000);
    } else if (!hasImporting && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [sites, load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErr('Name is required'); return; }
    if (mode === 'github' && (!form.github_owner.trim() || !form.github_repo.trim())) { setErr('Owner and repo are required'); return; }
    if (mode === 'upload' && !zipFile) { setErr('Choose a .zip file to upload'); return; }
    setSubmitting(true);
    setErr('');
    try {
      if (mode === 'github') {
        await api.post('/websites/import', { ...form, lob_id: lob.id });
      } else if (mode === 'upload') {
        const { data: site } = await api.post('/websites/blank', { name: form.name, lob_id: lob.id });
        const fd = new FormData();
        fd.append('file', zipFile);
        await api.post(`/websites/${site.id}/upload-zip`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/websites/blank', { name: form.name, lob_id: lob.id });
      }
      setForm({ name: '', github_owner: '', github_repo: '', github_branch: 'main' });
      setZipFile(null);
      setShowForm(false);
      load();
    } catch (e2) { setErr(formatApiError(e2)); }
    setSubmitting(false);
  };

  const reupload = async (s, file) => {
    if (!file) return;
    setUploadingId(s.id);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/websites/${s.id}/upload-zip`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (e) { setErr(formatApiError(e)); }
    setUploadingId('');
  };

  const reimport = async (s) => {
    try { await api.post(`/websites/${s.id}/reimport`); load(); } catch (e) { alert(formatApiError(e)); }
  };

  const rescan = async (s) => {
    try { await api.post(`/websites/${s.id}/rescan`); load(); } catch (e) { alert(formatApiError(e)); }
  };

  const setPrimary = async (s) => {
    try { await api.post(`/websites/${s.id}/set-primary`); load(); } catch (e) { alert(formatApiError(e)); }
  };

  const togglePublish = async (s) => {
    try { await api.patch(`/websites/${s.id}`, { is_published: !s.is_published }); load(); } catch (e) { alert(formatApiError(e)); }
  };

  const saveDomain = async (s) => {
    const domain = (domainDrafts[s.id] || '').trim();
    try {
      await api.patch(`/websites/${s.id}`, { custom_domain: domain });
      setSavedDomainId(s.id);
      setTimeout(() => setSavedDomainId(''), 2000);
      load();
    } catch (e) { alert(formatApiError(e)); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? This removes the site's files.`)) return;
    try { await api.delete(`/websites/${s.id}`); load(); } catch (e) { alert(formatApiError(e)); }
  };

  const moveLob = async (s, newLobId) => {
    if (!newLobId || newLobId === s.lob_id) return;
    try { await api.patch(`/websites/${s.id}`, { lob_id: newLobId }); load(); } catch (e) { alert(formatApiError(e)); }
  };

  const liveUrl = (s) => `${process.env.REACT_APP_BACKEND_URL}/api/public/websites/${s.id}/files/`;

  return (
    <>
      <div className="page-toolbar" data-testid="website-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Website</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <button className="cta-btn" style={{ background: lob.accent }} onClick={() => setShowForm((v) => !v)} data-testid="website-add-btn">
          <Plus size={14} style={{ marginRight: 4 }} />Add Website
        </button>
      </div>

      {err && <div className="module-banner err" data-testid="website-error">{err}</div>}

      <div className="content-area">
        <div className="module-banner" data-testid="website-integration-guide" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <Info size={14} style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <b>How the CRM bridge works:</b> The <b>Primary</b> site is served at your CRM's own root domain; other sites need a <b>Custom domain</b> assigned below (link that domain/subdomain to this app in Deploy → Link Domain first, then paste it here) — visitors to that domain will see this site instead. Any&nbsp;
            <code>&lt;form&gt;</code> on any site auto-submits as a Lead into that LOB's Contacts. Tag a "Buy" button or link with&nbsp;
            <code>data-crm-buy="product-slug"</code> to send buyers to your existing <code>/buy/&#123;slug&#125;</code> checkout. Add&nbsp;
            <code>data-crm-ignore</code> to a form to exclude it from auto-capture.
          </div>
        </div>

        {showForm && (
          <div className="feature-card" style={{ margin: '10px 0' }} data-testid="website-create-form-card">
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <button type="button" className={`dp-btn ${mode === 'github' ? 'primary' : ''}`} onClick={() => setMode('github')} data-testid="website-mode-github-btn">
                <Github size={12} style={{ marginRight: 4 }} />Import from GitHub
              </button>
              <button type="button" className={`dp-btn ${mode === 'blank' ? 'primary' : ''}`} onClick={() => setMode('blank')} data-testid="website-mode-blank-btn">
                <PenSquare size={12} style={{ marginRight: 4 }} />Build from Scratch
              </button>
              <button type="button" className={`dp-btn ${mode === 'upload' ? 'primary' : ''}`} onClick={() => setMode('upload')} data-testid="website-mode-upload-btn">
                <Upload size={12} style={{ marginRight: 4 }} />Upload ZIP
              </button>
            </div>
            <form onSubmit={submit} style={{ display: 'grid', gap: 8, maxWidth: 420 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faded)' }} htmlFor="website-name-input">Website name</label>
              <input id="website-name-input" className="dp-input" placeholder="e.g. First Flight Flip Mentor Program" value={form.name} data-testid="website-name-input"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
              {mode === 'github' && (
                <>
                  <input className="dp-input" placeholder="GitHub owner (e.g. legacytri)" value={form.github_owner} data-testid="website-owner-input"
                         onChange={(e) => setForm({ ...form, github_owner: e.target.value })} />
                  <input className="dp-input" placeholder="Repository name (e.g. mainwebsite)" value={form.github_repo} data-testid="website-repo-input"
                         onChange={(e) => setForm({ ...form, github_repo: e.target.value })} />
                  <input className="dp-input" placeholder="Branch" value={form.github_branch} data-testid="website-branch-input"
                         onChange={(e) => setForm({ ...form, github_branch: e.target.value })} />
                </>
              )}
              {mode === 'blank' && (
                <div className="sub">Creates an empty site record — the main agent (or you, via file access) places HTML/CSS/JS files directly on disk, then click "Rescan files" on the card below.</div>
              )}
              {mode === 'upload' && (
                <>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faded)', marginTop: 4 }} htmlFor="website-zip-input">Site files (.zip)</label>
                  <div className="sub">Upload a .zip of your site's HTML/CSS/JS/image files (max 25MB) — works directly from your browser, no dev access needed.</div>
                  <input id="website-zip-input" type="file" accept=".zip" className="dp-input" data-testid="website-zip-input"
                         onChange={(e) => setZipFile(e.target.files?.[0] || null)} />
                  {zipFile && <div className="sub" data-testid="website-zip-selected">Selected: {zipFile.name}</div>}
                </>
              )}
              <button className="cta-btn" style={{ background: lob.accent, justifySelf: 'start' }} disabled={submitting} data-testid="website-submit-btn">
                {submitting ? 'Working…' : mode === 'github' ? 'Import website' : mode === 'upload' ? 'Create & upload site' : 'Create blank site'}
              </button>
            </form>
          </div>
        )}

        {sites.length === 0 && !showForm ? (
          <div className="empty-state" data-testid="website-empty-state"><div className="ttl">No websites yet</div><div className="sub">Click "Add Website" to import from GitHub or build one from scratch.</div></div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {sites.map((s) => {
              const meta = STATUS_META[s.status] || STATUS_META.importing;
              const Icon = meta.icon;
              return (
                <div key={s.id} className="feature-card" style={{ margin: 0 }} data-testid={`website-card-${s.id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div>
                      <div className="ttl" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Globe size={14} /> {s.name}
                        <span className={`tag-pill ${meta.cls}`} data-testid={`website-status-${s.id}`}>
                          <Icon size={10} style={{ marginRight: 3, animation: meta.spin ? 'spin 1s linear infinite' : 'none' }} />{meta.label}
                        </span>
                        {s.is_primary && <span className="tag-pill ok" data-testid={`website-primary-badge-${s.id}`}><Star size={10} style={{ marginRight: 3 }} />Primary</span>}
                      </div>
                      <div className="sub">
                        {s.source === 'github' ? `${s.github_owner}/${s.github_repo} @ ${s.github_branch}` : 'Built from scratch'} · {s.file_count} files
                      </div>
                      {s.status === 'error' && <div className="sub" style={{ color: 'var(--error)', marginTop: 4 }} data-testid={`website-error-${s.id}`}>{s.last_import_error}</div>}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={s.is_published} onChange={() => togglePublish(s)} data-testid={`website-publish-toggle-${s.id}`} />
                      Published
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
                    <Link2 size={12} style={{ flexShrink: 0, color: 'var(--text-faded)' }} />
                    <input
                      className="dp-input" style={{ maxWidth: 260, fontSize: 11 }}
                      placeholder={s.is_primary ? 'yourdomain.com (optional — primary already shows at root)' : 'e.g. othersite.yourdomain.com'}
                      value={domainDrafts[s.id] ?? ''}
                      onChange={(e) => setDomainDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                      data-testid={`website-domain-input-${s.id}`}
                    />
                    <button className="dp-btn" onClick={() => saveDomain(s)} data-testid={`website-domain-save-${s.id}`}>
                      {savedDomainId === s.id ? <><Check size={10} style={{ marginRight: 4, color: 'var(--ok)' }} />Saved</> : 'Save domain'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                    <ArrowRightLeft size={12} style={{ flexShrink: 0, color: 'var(--text-faded)' }} />
                    <select
                      className="dp-input" style={{ maxWidth: 260, fontSize: 11 }}
                      value={s.lob_id}
                      onChange={(e) => moveLob(s, e.target.value)}
                      data-testid={`website-move-lob-${s.id}`}
                    >
                      {lobs.map((l) => (
                        <option key={l.id} value={l.id}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 12, flexWrap: 'wrap' }}>
                    {s.status === 'ready' && (
                      <a className="dp-btn primary" href={liveUrl(s)} target="_blank" rel="noreferrer" data-testid={`website-view-live-${s.id}`}>
                        <ExternalLink size={10} style={{ marginRight: 4 }} />View live
                      </a>
                    )}
                    {!s.is_primary && s.status === 'ready' && (
                      <button className="dp-btn" onClick={() => setPrimary(s)} data-testid={`website-set-primary-${s.id}`}>
                        <Star size={10} style={{ marginRight: 4 }} />Set as Primary
                      </button>
                    )}
                    {s.source === 'github' ? (
                      <button className="dp-btn" onClick={() => reimport(s)} disabled={s.status === 'importing'} data-testid={`website-reimport-${s.id}`}>
                        <RefreshCw size={10} style={{ marginRight: 4 }} />Re-import latest
                      </button>
                    ) : (
                      <button className="dp-btn" onClick={() => rescan(s)} data-testid={`website-rescan-${s.id}`}>
                        <RefreshCw size={10} style={{ marginRight: 4 }} />Rescan files
                      </button>
                    )}
                    {s.source !== 'github' && (
                      <>
                        <input
                          type="file" accept=".zip" ref={(el) => { reuploadRefs.current[s.id] = el; }}
                          style={{ display: 'none' }}
                          onChange={(e) => { reupload(s, e.target.files?.[0]); e.target.value = ''; }}
                          data-testid={`website-reupload-input-${s.id}`}
                        />
                        <button className="dp-btn" onClick={() => reuploadRefs.current[s.id]?.click()} disabled={uploadingId === s.id} data-testid={`website-reupload-btn-${s.id}`}>
                          <Upload size={10} style={{ marginRight: 4 }} />{uploadingId === s.id ? 'Uploading…' : 'Upload/Replace ZIP'}
                        </button>
                      </>
                    )}
                    <button className="dp-btn" style={{ color: 'var(--error)' }} onClick={() => del(s)} data-testid={`website-delete-${s.id}`}>
                      <Trash2 size={10} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default Website;
